// src/firebase/scheduleService.js
import {
  collection,
  doc,
  setDoc,
  deleteDoc,
  getDocs,
  query,
  where,
  orderBy,
} from "firebase/firestore";
import { db } from "./firebase";
import { encryptJSON, decryptJSON } from "../utils/crypto";

const SCHEDULES_COLLECTION = "userSchedules";
const toDate = (v) => (v?.toDate ? v.toDate() : v);
const DATA_VERSION = 1;
const META_KEYS = [
  "id",
  "userId",
  "createdAt",
  "updatedAt",
  "version",
  "iv",
  "ciphertext",
];

const aad = (userId, scheduleId) => `schedule:${userId}:${scheduleId}`;

const payloadOf = (data) => {
  const p = { ...data };
  META_KEYS.forEach((k) => delete p[k]);
  return p;
};

/** Save new schedule. dek = CryptoKey from key context. */
export const saveSchedule = async (userId, scheduleData, dek) => {
  try {
    const ref = doc(collection(db, SCHEDULES_COLLECTION));
    const now = new Date();
    const payload = payloadOf(scheduleData);
    const { iv, ciphertext } = await encryptJSON(
      dek,
      payload,
      aad(userId, ref.id),
    );

    await setDoc(ref, {
      userId,
      createdAt: now,
      updatedAt: now,
      version: DATA_VERSION,
      iv,
      ciphertext,
    });
    return { id: ref.id, ...payload, userId, createdAt: now, updatedAt: now };
  } catch (error) {
    console.error("Error saving schedule:", error);
    throw error;
  }
};

/**
 * Update. scheduleData must be FULL schedule object (not partial).
 * Pass createdAt inside scheduleData (comes from getUserSchedules) to keep it.
 */
export const updateSchedule = async (scheduleId, scheduleData, userId, dek) => {
  try {
    const now = new Date();
    const payload = payloadOf(scheduleData);
    const { iv, ciphertext } = await encryptJSON(
      dek,
      payload,
      aad(userId, scheduleId),
    );
    const createdAt = scheduleData.createdAt ?? now;

    await setDoc(doc(db, SCHEDULES_COLLECTION, scheduleId), {
      userId,
      createdAt,
      updatedAt: now,
      version: DATA_VERSION,
      iv,
      ciphertext,
    });
    return { id: scheduleId, ...payload, userId, createdAt, updatedAt: now };
  } catch (error) {
    console.error("Error updating schedule:", error);
    throw error;
  }
};

/** Get + decrypt all. Legacy plaintext docs returned as-is with _legacy: true. */
export const getUserSchedules = async (userId, dek) => {
  try {
    const q = query(
      collection(db, SCHEDULES_COLLECTION),
      where("userId", "==", userId),
      orderBy("createdAt", "desc"),
    );
    const snapshot = await getDocs(q);

    const results = await Promise.all(
      snapshot.docs.map(async (d) => {
        const data = d.data();
        const createdAt = toDate(data.createdAt);
        const updatedAt = toDate(data.updatedAt);

        if (!data.ciphertext)
          return { id: d.id, ...data, createdAt, updatedAt, _legacy: true };
        try {
          const payload = await decryptJSON(dek, data, aad(userId, d.id));
          return {
            id: d.id,
            ...payload,
            userId: data.userId,
            createdAt,
            updatedAt,
          };
        } catch (err) {
          console.error("Decrypt failed for schedule", d.id);
          return null;
        }
      }),
    );
    return results.filter(Boolean);
  } catch (error) {
    console.error("Error getting schedules:", error);
    throw error;
  }
};

/** One-time: encrypt old plaintext docs. Call once after first unlock. */
export const migrateLegacySchedules = async (userId, dek) => {
  const q = query(
    collection(db, SCHEDULES_COLLECTION),
    where("userId", "==", userId),
  );
  const snapshot = await getDocs(q);
  let migrated = 0;

  for (const d of snapshot.docs) {
    const data = d.data();
    if (data.ciphertext) continue;
    const payload = payloadOf(data);
    const { iv, ciphertext } = await encryptJSON(
      dek,
      payload,
      aad(userId, d.id),
    );
    await setDoc(d.ref, {
      userId,
      createdAt: data.createdAt ?? new Date(),
      updatedAt: data.updatedAt ?? new Date(),
      version: DATA_VERSION,
      iv,
      ciphertext,
    }); // full replace → plaintext fields gone
    migrated++;
  }
  return migrated;
};

export const deleteSchedule = async (scheduleId) => {
  try {
    await deleteDoc(doc(db, SCHEDULES_COLLECTION, scheduleId));
    return true;
  } catch (error) {
    console.error("Error deleting schedule:", error);
    throw error;
  }
};
